import './spellbook.css';

import {
  getSpell,
  SPELL_BAR_SIZE,
  type SpellBarRejected,
  type SpellDefinition,
  type SpellsEvent,
  spellsOf,
} from '@shefira/shared';

import {
  bindingLabel,
  bindingOf,
  isAction,
  type KeyAction,
  onBindingsChange,
} from '../settings/keybindings.js';
import { closeButton, hudShortcutButton } from './hud.js';
import { element, kitTitle } from './kit.js';
import { DRAG_TYPE, parseDrag, spellRow } from './spellbook-list.js';
import { uiFormat, uiText } from './text.js';
import { isTouch } from './touch.js';

export interface SpellbookPanelOptions {
  classId: string;
  /** Intent: fight with these four spells (`set_spell_bar`); the server may refuse. */
  onSetBar: (bar: (string | null)[]) => void;
}

const SLOT_ACTIONS = [
  'spell1',
  'spell2',
  'spell3',
  'spell4',
] as const satisfies readonly KeyAction[];

/** What the window says when the server keeps the bar as it was. */
const BAR_REFUSALS: Record<SpellBarRejected['reason'], string> = {
  'not-learned': 'Tu ne connais pas encore ce sort.',
  duplicate: 'Ce sort est déjà dans ta barre.',
  'wrong-size': 'Barre de sorts invalide.',
  'in-combat': 'Impossible en combat.',
  dead: 'Impossible à terre.',
};

const HINT_MOUSE =
  'Glisse un sort sur un emplacement, ou clique le sort puis l’emplacement. Clic droit pour vider.';
const HINT_TOUCH = 'Touche un sort, puis l’emplacement où le mettre.';

/** What is picked up for a placement by clicks or taps: a spell of the list, or a slot's. */
type Pick = { from: 'list'; id: string } | { from: 'slot'; index: number };

/**
 * The grimoire (ADR 0048): every spell of the class in the order it is
 * learnt, those known ready to go on the bar, the others greyed with what
 * teaches them (a level, a region's quest). The four slots of the bar sit at
 * the top; a spell goes on it by drag and drop with a mouse, or by a tap on
 * the spell then on the slot with a finger, and the whole bar leaves as one
 * `set_spell_bar`. Only a view of what the server said in `spells`: the bar
 * shown is the server's once it answers, and a refusal puts it back.
 */
export class SpellbookPanel {
  private readonly root: HTMLElement;
  private readonly slotsRow: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly clear: HTMLButtonElement;
  private readonly list: HTMLElement;
  private readonly button: HTMLButtonElement;
  private readonly grimoire: SpellDefinition[];
  private known = new Set<string>();
  /** The bar as the server last settled it, and as shown (ahead of the server by one change). */
  private serverBar: (string | null)[] = Array.from({ length: SPELL_BAR_SIZE }, () => null);
  private bar: (string | null)[] = [...this.serverBar];
  private level = 1;
  private pick: Pick | null = null;
  private refusal: string | null = null;
  private readonly unbind: () => void;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    if (event.key === 'Escape' && !this.root.hidden) {
      this.close();
      return;
    }
    if (!isAction(event, 'spellbook')) return;
    event.preventDefault();
    this.toggle();
  };

  constructor(private readonly options: SpellbookPanelOptions) {
    this.grimoire = spellsOf(options.classId);
    this.root = element('section', 'spellbook');
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Grimoire'));

    const title = kitTitle(element('h2', 'spellbook__title', 'Grimoire'), 'spellbook');
    title.append(
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );

    this.slotsRow = element('div', 'spellbook__slots');
    const tools = element('div', 'spellbook__tools');
    this.hint = element('p', 'spellbook__hint');
    this.hint.setAttribute('aria-live', 'polite');
    this.clear = element('button', 'spellbook__clear', 'Retirer');
    this.clear.type = 'button';
    this.clear.addEventListener('click', () => {
      if (this.pick?.from !== 'slot') return;
      this.clearSlot(this.pick.index);
    });
    tools.append(this.hint, this.clear);
    this.list = element('ul', 'spellbook__list');
    this.root.append(title, this.slotsRow, tools, this.list);
    document.body.append(this.root);

    this.button = hudShortcutButton('spellbook', 'Grimoire', 'spellbook');
    this.button.addEventListener('click', () => {
      this.toggle();
    });
    window.addEventListener('keydown', this.onKeyDown);
    this.unbind = onBindingsChange(() => {
      this.render();
    });
    this.render();
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    if (this.root.hidden) this.open();
    else this.close();
  }

  open(): void {
    this.root.hidden = false;
    this.pick = null;
    this.refusal = null;
    this.render();
  }

  close(): void {
    this.root.hidden = true;
    this.pick = null;
  }

  /** The server's word: the spells known and the bar they fight with. */
  setSpells(event: SpellsEvent): void {
    this.known = new Set(event.known);
    this.serverBar = [...event.bar];
    this.bar = [...event.bar];
    this.refusal = null;
    this.render();
  }

  /** The level, for the requirements of the spells still locked. */
  setLevel(level: number): void {
    if (level === this.level) return;
    this.level = level;
    this.render();
  }

  /** The server kept the bar as it was: show it again, and say why. */
  showRefusal(reason: SpellBarRejected['reason']): void {
    this.bar = [...this.serverBar];
    this.refusal = BAR_REFUSALS[reason];
    this.pick = null;
    this.render();
  }

  // ---- Changes, each sent whole ----

  private commit(next: (string | null)[]): void {
    this.pick = null;
    this.refusal = null;
    if (next.every((id, index) => id === this.bar[index])) {
      this.render();
      return;
    }
    this.bar = next;
    this.render();
    this.options.onSetBar([...next]);
  }

  /** A spell into a slot; already on the bar, it trades places with what the slot held. */
  private place(id: string, index: number): void {
    if (!this.known.has(id)) return;
    const next = [...this.bar];
    const from = next.indexOf(id);
    if (from === index) {
      this.commit(next);
      return;
    }
    if (from !== -1) next[from] = next[index] ?? null;
    next[index] = id;
    this.commit(next);
  }

  private swap(a: number, b: number): void {
    const next = [...this.bar];
    const held = next[a] ?? null;
    next[a] = next[b] ?? null;
    next[b] = held;
    this.commit(next);
  }

  private clearSlot(index: number): void {
    const next = [...this.bar];
    next[index] = null;
    this.commit(next);
  }

  /** A click or a tap on a slot: drop what is picked there, or pick up what it holds. */
  private onSlotClick(index: number): void {
    const pick = this.pick;
    if (pick?.from === 'list') {
      this.place(pick.id, index);
      return;
    }
    if (pick?.from === 'slot') {
      if (pick.index === index) this.pick = null;
      else {
        this.swap(pick.index, index);
        return;
      }
    } else if (this.bar[index] !== null) {
      this.pick = { from: 'slot', index };
    }
    this.render();
  }

  private onSpellClick(spell: SpellDefinition): void {
    if (!this.known.has(spell.id)) return;
    const already = this.pick?.from === 'list' && this.pick.id === spell.id;
    this.pick = already ? null : { from: 'list', id: spell.id };
    this.refusal = null;
    this.render();
  }

  // ---- Drawing ----

  private render(): void {
    this.renderSlots();
    this.renderHint();
    this.renderList();
  }

  private renderSlots(): void {
    this.slotsRow.replaceChildren();
    SLOT_ACTIONS.forEach((action, index) => {
      const id = this.bar[index] ?? null;
      const spell = id === null ? undefined : getSpell(id);
      const slot = element('button', 'spellbook__slot');
      slot.type = 'button';
      if (this.pick?.from === 'slot' && this.pick.index === index) {
        slot.classList.add('spellbook__slot--picked');
      }
      if (this.pick !== null && !(this.pick.from === 'slot' && this.pick.index === index)) {
        slot.classList.add('spellbook__slot--target');
      }
      const icon = element('span', 'spellbook__icon');
      if (spell !== undefined) icon.style.backgroundImage = `url('/assets/ui/${spell.icon}.png')`;
      const key = element('span', 'spellbook__key');
      key.textContent = uiText(bindingLabel(bindingOf(action)), 'key');
      slot.append(icon, key);
      const label =
        spell === undefined
          ? uiFormat('Emplacement {slot} : libre', { slot: index + 1 })
          : uiFormat('Emplacement {slot} : {name}', { slot: index + 1, name: spell.name });
      slot.title = uiText(label);
      slot.setAttribute('aria-label', uiText(label));
      slot.addEventListener('click', () => {
        this.onSlotClick(index);
      });
      slot.addEventListener('contextmenu', (event) => {
        event.preventDefault();
        if (this.bar[index] !== null) this.clearSlot(index);
      });
      if (spell !== undefined && !isTouch()) {
        slot.draggable = true;
        slot.addEventListener('dragstart', (event) => {
          event.dataTransfer?.setData(DRAG_TYPE, JSON.stringify({ id: spell.id, index }));
          if (event.dataTransfer !== null) event.dataTransfer.effectAllowed = 'move';
        });
      }
      this.acceptDrops(slot, index);
      this.slotsRow.append(slot);
    });
  }

  private acceptDrops(slot: HTMLElement, index: number): void {
    slot.addEventListener('dragover', (event) => {
      if (!(event.dataTransfer?.types.includes(DRAG_TYPE) ?? false)) return;
      event.preventDefault();
      slot.classList.add('spellbook__slot--over');
    });
    slot.addEventListener('dragleave', () => {
      slot.classList.remove('spellbook__slot--over');
    });
    slot.addEventListener('drop', (event) => {
      event.preventDefault();
      slot.classList.remove('spellbook__slot--over');
      const raw = event.dataTransfer?.getData(DRAG_TYPE) ?? '';
      const dragged = parseDrag(raw);
      if (dragged === null) return;
      if (dragged.index !== undefined) this.swap(dragged.index, index);
      else this.place(dragged.id, index);
    });
  }

  private renderHint(): void {
    this.hint.classList.toggle('spellbook__hint--refusal', this.refusal !== null);
    const picked = this.pick;
    let text: string;
    if (this.refusal !== null) text = this.refusal;
    else if (picked?.from === 'list') {
      const spell = getSpell(picked.id);
      text =
        spell === undefined
          ? uiText('Choisis l’emplacement de ce sort.')
          : uiFormat('Choisis l’emplacement de {name}.', { name: spell.name });
    } else if (picked?.from === 'slot') {
      text = 'Choisis un autre emplacement pour échanger, ou retire le sort.';
    } else text = isTouch() ? HINT_TOUCH : HINT_MOUSE;
    this.hint.textContent = uiText(text);
    this.clear.hidden = picked?.from !== 'slot';
  }

  private renderList(): void {
    const scroll = this.list.scrollTop;
    this.list.replaceChildren();
    for (const spell of this.grimoire) {
      this.list.append(
        spellRow(spell, {
          known: this.known.has(spell.id),
          onBar: this.bar.includes(spell.id),
          picked: this.pick?.from === 'list' && this.pick.id === spell.id,
          level: this.level,
          draggable: !isTouch(),
          onPick: () => {
            this.onSpellClick(spell);
          },
        }),
      );
    }
    this.list.scrollTop = scroll;
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.unbind();
    this.root.remove();
    this.button.remove();
  }
}
